import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import { Switch as BaseSwitch } from "@base-ui/react/switch";
import type { ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon } from "../icons.tsx";

interface ToggleProps {
  /** Visible label; clicking it toggles the control. */
  label: ReactNode;
  /** Secondary line under the label. */
  description?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  name?: string;
  value?: string;
  required?: boolean;
  className?: string;
}

function ToggleLabel({ label, description }: { label: ReactNode; description?: ReactNode }) {
  return (
    <span className="pmu-toggle__text">
      <span className="pmu-toggle__label">{label}</span>
      {description && <span className="pmu-toggle__description">{description}</span>}
    </span>
  );
}

export interface CheckboxProps extends ToggleProps {
  indeterminate?: boolean;
}

export function Checkbox({ label, description, onCheckedChange, className, ...rest }: CheckboxProps) {
  return (
    <label className={cx("pmu-toggle", className)}>
      <BaseCheckbox.Root
        {...rest} className="pmu-checkbox"
        onCheckedChange={onCheckedChange ? (c) => onCheckedChange(c) : undefined}
      >
        <BaseCheckbox.Indicator className="pmu-checkbox__indicator" keepMounted={false}>
          <Icon name={rest.indeterminate ? "minus" : "check"} />
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>
      <ToggleLabel label={label} description={description} />
    </label>
  );
}

export type SwitchProps = ToggleProps;

export function Switch({ label, description, onCheckedChange, className, ...rest }: SwitchProps) {
  return (
    <label className={cx("pmu-toggle pmu-toggle--switch", className)}>
      <ToggleLabel label={label} description={description} />
      <BaseSwitch.Root
        {...rest} className="pmu-switch"
        onCheckedChange={onCheckedChange ? (c) => onCheckedChange(c) : undefined}
      >
        <BaseSwitch.Thumb className="pmu-switch__thumb" />
      </BaseSwitch.Root>
    </label>
  );
}
